import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Obra } from '../types';
import { useAuth } from './AuthContext';

interface ObrasContextType {
  obras: Obra[];
  obraSeleccionada: string;
  setObraSeleccionada: (obraId: string) => void;
  loading: boolean;
  fetchObras: () => Promise<void>;
  updateObra: (id: string, data: Partial<Obra>) => Promise<void>;
}

const ObrasContext = createContext<ObrasContextType | undefined>(undefined);

export const ObrasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { usuario } = useAuth();
  const [obras, setObras] = useState<Obra[]>([]);
  const [obraSeleccionada, setObraSeleccionada] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchObras = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'obras'));
      let obrasData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          fechaInicio: data.fechaInicio?.toDate ? data.fechaInicio.toDate() : new Date(),
          fechaLimite: data.fechaLimite?.toDate ? data.fechaLimite.toDate() : new Date()
        };
      }) as Obra[];

      // Ocultar obras completadas de los selectores (contexto global)
      obrasData = obrasData.filter(obra => obra.estado !== 'completada');

      setObras(obrasData);

      // Lógica de selección por defecto
      if (usuario?.obrasAsignadas && usuario.obrasAsignadas.length > 0) {
        // Si es editor y tiene solo una obra asignada, seleccionarla por defecto
        // Verificamos 'EDITOR' (según tipos) y 'editor' (por robustez)
        if ((usuario.role === 'EDITOR' || (usuario.role as string) === 'editor') && usuario.obrasAsignadas.length === 1) {
          setObraSeleccionada(usuario.obrasAsignadas[0]);
        }
        // Si es contratista, comportamiento existente (seleccionar la primera alfabéticamente)
        else if (usuario.role === 'contratista') {
          const obrasPermitidas = obrasData
            .filter(obra => usuario.obrasAsignadas.includes(obra.id))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));

          if (obrasPermitidas.length > 0) {
            setObraSeleccionada(obrasPermitidas[0].id);
          }
        }
        // En cualquier otro caso (admin, editor con múltiples obras), se mantiene '' (Todas las obras)
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching obras:", error);
      setLoading(false);
    }
  }

  const updateObra = async (id: string, data: Partial<Obra>) => {
    try {
      const obraRef = doc(db, 'obras', id);
      await updateDoc(obraRef, data);

      // Update local state
      setObras(prev => prev.map(o => o.id === id ? { ...o, ...data } : o));
    } catch (error) {
      console.error("Error updating obra:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchObras();
  }, [usuario]);

  return (
    <ObrasContext.Provider value={{
      obras,
      obraSeleccionada,
      setObraSeleccionada,
      loading,
      fetchObras,
      updateObra
    }}>
      {children}
    </ObrasContext.Provider>
  );
};

export const useObras = () => {
  const context = useContext(ObrasContext);
  if (context === undefined) {
    throw new Error('useObras debe ser usado dentro de un ObrasProvider');
  }
  return context;
};